<script setup lang="ts">
import { ref, watch } from 'vue'
import { rejectMobileAction, verifyMobileAction } from '@/api/functions/users/phone-actions'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  userData: { type: Object },
  uuid: { type: String, required: true },
})
const emit = defineEmits(['refreshUserData'])
const toast = useToast()

// States
const isShowEditMobile = ref(false)
const showRejectModal = ref(false)
const showAcceptModal = ref(false)
const isAccepting = ref(false)
const isRejecting = ref(false)

const mobile = ref(props.userData?.mobile || '')
const selectedCountry = ref(props.userData?.country?.id || '')

// Update local values if props.userData changes
watch(
  () => props.userData,
  (newVal) => {
    mobile.value = newVal?.mobile || ''
    selectedCountry.value = newVal?.country?.id || ''
  },
  { immediate: true }
)

// Mutations
// const saveMutation = useMutationApi({
//     mutationFn: () => updateMobileAction(props.uuid, { mobile: mobile.value, countryId: selectedCountry.value }),
//     onMutate: () => toast.info('در حال بروزرسانی شماره موبایل...'),
//     onSuccess: (res) => {
//         toast.success(res.data?.meta?.message || 'عملیات با موفقیت انجام شد');
//         emit('refreshUserData');
//         isShowEditMobile.value = false;
//     },
//     onError: (error: Error) => toast.error(error?.message || 'خطا در بروزرسانی شماره موبایل')
// });

const acceptMutation = useMutationApi({
  mutationFn: async () => {
    isAccepting.value = true

    try {
      return await verifyMobileAction(props.uuid)
    } finally {
      isAccepting.value = false
    }
  },

  onMutate: () => {
    toast.info('در حال تایید شماره موبایل...')
  },

  onSuccess: (res) => {
    toast.success(res.data?.meta?.message || 'شماره موبایل با موفقیت تایید شد')

    emit('refreshUserData')
    showAcceptModal.value = false
  },

  onError: (error: Error) => {
    toast.error(error?.message || 'خطا در تایید شماره موبایل')
  },
})

const rejectMutation = useMutationApi({
  mutationFn: async () => {
    isRejecting.value = true

    try {
      return await rejectMobileAction(props.uuid)
    } finally {
      isRejecting.value = false
    }
  },

  onMutate: () => {
    toast.info('در حال رد شماره موبایل...')
  },

  onSuccess: (res) => {
    toast.success(res.data?.meta?.message || 'شماره موبایل با موفقیت رد شد')

    emit('refreshUserData')
    showRejectModal.value = false
    mobile.value = ''
  },

  onError: (error: Error) => {
    toast.error(error?.message || 'خطا در رد شماره موبایل')
  },
})

// Handlers
// const handleSave = () => saveMutation.mutate();
const handleAccept = () => acceptMutation.mutate()
const handleReject = () => rejectMutation.mutate()
</script>

<template>
    <div class="bg-white rounded-xl grid lg:grid-cols-3 my-6 lg:h-28 p-8 gap-10 lg:gap-6">

        <!-- Modals -->
        <ConfirmModal v-model="showRejectModal" title="رد شماره موبایل"
            :message="'آیا از رد شماره موبایل اطمینان دارید؟'" confirm-text="بله رد شود" cancel-text="بازگشت"
            @confirm="handleReject" @cancel="showRejectModal = false" />
        <ConfirmModal v-model="showAcceptModal" title="تایید شماره موبایل"
            :message="'آیا از تایید شماره موبایل اطمینان دارید؟'" confirm-text="بله تایید شود" cancel-text="بازگشت"
            @confirm="handleAccept" @cancel="showAcceptModal = false" />

        <!-- Mobile Input -->
        <div class="w-full flex items-center justify-between gap-2 text-sm">
            <span class="text-[#2d5396] text-nowrap">شماره موبایل:</span>
            <div v-if="!isShowEditMobile" class="flex items-center gap-2 font-fa text-[#2d5396]">
                <img v-if="userData?.mobile" src="/svg/call-dropped-rounded-svgrepo-com.svg" width="22" height="22" />
                <span dir="ltr">
                    {{ userData?.countryCode && userData?.mobile ? userData?.countryCode : undefined }} {{
                        userData?.mobile
                            ? userData?.mobile : 'وارد نشده' }}</span>
            </div>
            <div v-else>
                <InputWithCountrySelect v-model="mobile" :countryValue="selectedCountry"
                    @update:countryValue="(val: string) => selectedCountry = val" name="mobile"
                    label="شماره موبایل جدید را وارد کنید" />
            </div>
        </div>

        <!-- Status -->
        <div class="w-full flex items-center justify-between text-sm">
            <span class="text-[#2d5396]">وضعیت شماره موبایل:</span>
            <span class="font-fa text-[#1b325a]">{{ userData?.mobile ? 'تایید شده' : 'تایید نشده' }}</span>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-2">
            <Button @click="isShowEditMobile = !isShowEditMobile" :isAdmin="true"
                class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal">
                ویرایش
            </Button>

            <Button @click="showAcceptModal = true" :isAdmin="true"
                :disabled="!mobile || userData?.status !== 'MOBILE_VERIFICATION_PENDING' || isAccepting || isRejecting"
                :loading="isAccepting" class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal">
                {{ isAccepting ? 'در حال بروزرسانی...' : 'تایید' }}
            </Button>


            <Button @click="showRejectModal = true" :isAdmin="true"
                :disabled="!mobile || userData?.status !== 'MOBILE_VERIFICATION_PENDING' || isAccepting || isRejecting"
                :loading="isRejecting"
                class="w-1/2 lg:w-auto border-2 disabled:border-0 disabled:cursor-not-allowed rounded-[10px] px-4 h-10! py-0 text-[13px] font-normal me-2">
                {{ isRejecting ? 'در حال بروزرسانی...' : 'رد' }}
            </Button>

        </div>
    </div>
</template>
